import React, { useEffect, useState } from 'react';
import { Alert, Form, Table } from 'react-bootstrap';
import Select from 'react-select';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { ActionButtons } from '../../../common/components';
import { WeekdayPriceChangeRuleItem } from '../../../common/types/rates.types';
import { PRICE_CHANGE_TYPES, WEEKDAYS_SHORT } from '../../../common/constants';
import WeekdayPriceChangeRulesForm from './WeekdayPriceChangeRulesForm';
import {
  addWeekdayPriceChangeRule,
  getWeekdayPriceChangeRulesDataAsync,
  removeWeekdayPriceChangeRule,
  selectWeekdayPriceChangeRuleItemsData,
  selectWeekdayPriceChangeRuleItemsDataLoadingStatus,
  updateSeasonRateWeekdayPriceChangeRulesDataAsync,
  updateWeekdayPriceChangeRule,
} from '../seasonRateWeekdayRulesSlice';

function SeasonRateWeekdayPriceChangeRules({
  seasonRateId,
  hasWriteAccess,
}: {
  seasonRateId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const weekdayPriceChangeRuleItemsData = useAppSelector(
    selectWeekdayPriceChangeRuleItemsData,
  );
  const weekdayPriceChangeRuleItemsDataStatus = useAppSelector(
    selectWeekdayPriceChangeRuleItemsDataLoadingStatus,
  );

  const initialData: WeekdayPriceChangeRuleItem = {
    weekday: -1,
    priceChangeType: 0,
    difference: 0,
    percent: 0,
    isActive: true,
    isCopyable: true,
  };

  const weekdayOptions = WEEKDAYS_SHORT.map(item => {
    return {
      value: item.weekdayInt,
      label: item.weekdayString,
    };
  });

  const [selectedWeekday, setSelectedWeekday] = useState(-1);
  const [data, setData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    dispatch(getWeekdayPriceChangeRulesDataAsync(seasonRateId));
  }, [seasonRateId, dispatch]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setData(newData);
  };

  const handleSelectChange = (selectedOption, name) => {
    const path = name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] = selectedOption.value;
    setData(newData);
  };

  const handleWeekdayChange = selectedOption => {
    setSelectedWeekday(selectedOption.value);

    const existingRule =
      weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
        rule => rule?.weekday === selectedOption.value,
      );

    if (existingRule && existingRule.weekday) {
      setData(existingRule);
    } else {
      const newData = initialData;
      newData.weekday = selectedOption.value;
      setData(newData);
    }
  };

  const addOrUpdateRule = () => {
    const existingRule =
      weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
        rule => rule?.weekday === data.weekday,
      );

    if (!existingRule) {
      dispatch(addWeekdayPriceChangeRule(data));
    } else {
      dispatch(updateWeekdayPriceChangeRule(data));
    }
  };

  const removeRule = () => {
    const existingRule =
      weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
        rule => rule?.weekday === data.weekday,
      );

    if (existingRule) {
      dispatch(removeWeekdayPriceChangeRule(existingRule.weekday));
    }
  };

  const onSubmit = () => {
    dispatch(
      updateSeasonRateWeekdayPriceChangeRulesDataAsync(
        weekdayPriceChangeRuleItemsData,
      ),
    );
    setSubmitted(true);
  };

  const renderCurrentRules = (
    <>
      {WEEKDAYS_SHORT.map(weekdayItem => {
        const existingRule =
          weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
            rule => rule?.weekday === weekdayItem.weekdayInt,
          );

        if (!existingRule) {
          return null;
        }

        const priceChangeType = PRICE_CHANGE_TYPES.find(
          type => type.priceChangeType === existingRule.priceChangeType,
        );

        return (
          <tr key={`weekdayPriceChangeRule-${weekdayItem.weekdayInt}`}>
            <td>
              <strong>{weekdayItem.weekdayString}</strong>
            </td>
            <td>{priceChangeType.description}</td>
            <td>
              {existingRule.priceChangeType === 1
                ? existingRule.difference
                : existingRule.percent}
            </td>
            <td>{existingRule.isCopyable ? 'Kyllä' : 'Ei'}</td>
            <td>{existingRule.isActive ? 'Kyllä' : 'Ei'}</td>
          </tr>
        );
      })}
    </>
  );

  return (
    <>
      <Form.Group className="mb-24">
        <Form.Label>Viikonpäivä</Form.Label>
        <Select
          inputId="weekday"
          name="weekday"
          placeholder="Valitse viikonpäivä"
          isClearable
          options={weekdayOptions}
          onChange={option => handleWeekdayChange(option)}
          value={weekdayOptions.filter(
            option => option.value === selectedWeekday,
          )}
          classNames={{
            control: state => 'form-control form-control-react-select',
          }}
          isDisabled={!hasWriteAccess}
        />
      </Form.Group>

      {selectedWeekday > -1 && (
        <WeekdayPriceChangeRulesForm
          addOrUpdateRule={addOrUpdateRule}
          removeRule={removeRule}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          selectedWeekday={selectedWeekday}
          data={data}
          hasWriteAccess={hasWriteAccess}
        />
      )}

      <hr className="hr-light my-32" />

      <h4 className="h5 mb-24">Nykyiset hintamuutokset eri viikonpäiville</h4>
      <div className="table-responsive-lg mb-40">
        <Table bordered>
          <thead>
            <tr>
              <th>Viikonpäivä</th>
              <th>Muutoksen tyyppi</th>
              <th>Muutos</th>
              <th>Kopioitava</th>
              <th>Aktiivinen</th>
            </tr>
          </thead>
          <tbody>{renderCurrentRules}</tbody>
        </Table>
      </div>

      <ActionButtons
        HandleSave={onSubmit}
        SaveButtonText="Tallenna tehdyt muutokset"
        HideSaveButton={!hasWriteAccess}
        HideCancelButton
        DeleteButtonText=""
      />
      {submitted && (
        <div className="mt-2 pt-2">
          <Alert variant="success">Tiedot tallennettu!</Alert>
        </div>
      )}
    </>
  );
}

export default SeasonRateWeekdayPriceChangeRules;
